import { catalogApiRef } from '@mainstage/plugin-catalog-react';
import {
  SystemPrototypeTemplateEntity,
  WitboostDomain,
} from '@agilelab/plugin-wb-builder-common';
import {
  SystemPrototype,
  systemPrototypeApiRef,
} from '@agilelab/plugin-wb-system-prototype-common';
import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { SystemPrototypeDetailsCustomView } from '../CustomViews';
import { EditSystemPrototypeSidebar } from '../EditSystemPrototypeSidebar';
import { SYSTEM_PROTOTYPES_QUERY_KEY } from '../hooks/constants';
import { SystemPrototypeDetaisContextProvider } from './SystemPrototypeDetaisContext';
import { LayoutOptions } from '@agilelab/plugin-wb-scaffolder';
import useAsync from 'react-use/lib/useAsync';
import { expandEntityRef } from '@agilelab/plugin-wb-platform-common';

export interface SystemPrototypeDetailsProps {
  prototype: SystemPrototype;
  template?: SystemPrototypeTemplateEntity;
  layouts?: LayoutOptions[];
}

export const SystemPrototypeDetails: FC<SystemPrototypeDetailsProps> = ({
  prototype,
  template,
  layouts,
}) => {
  const [editedSection, setEditedSection] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const systemPrototypeApi = useApi(systemPrototypeApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  // Fetch referenced entities and their display names
  const { value: enrichedPrototype } = useAsync(async () => {
    if (!prototype) return prototype;

    const [typeEntity, domainEntity] = await Promise.all([
      prototype.systemPrototypeType
        ? catalogApi.getEntityByRef(prototype.systemPrototypeType)
        : null,
      prototype.domain
        ? catalogApi.getEntityByRef(expandEntityRef(prototype.domain))
        : null,
    ]);

    const domain = domainEntity as WitboostDomain | null;

    return {
      ...prototype,
      systemPrototypeTypeDisplayName:
        typeEntity?.metadata?.displayName || typeEntity?.metadata?.name || '',
      domainDisplayName:
        domain?.spec?.mesh?.name || domain?.metadata?.name || '',
    };
  }, [prototype, catalogApi]);

  const handleCloseEdit = () => {
    setEditedSection(null);
  };

  const handleSaveEdit = async (updatedData: Partial<SystemPrototype>) => {
    try {
      await systemPrototypeApi.updateSystemPrototype(
        prototype.id,
        {
          name: updatedData.name,
          owner: updatedData.owner,
          systemPrototypeType: updatedData.systemPrototypeType,
          domain: updatedData.domain,
          description: updatedData.description,
          purpose: updatedData.purpose,
          status: updatedData.status,
          parameters: updatedData.parameters,
        },
        await identityApi.getCredentials(),
      );
      queryClient.invalidateQueries({
        queryKey: [SYSTEM_PROTOTYPES_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: ['system-prototype', prototype.id],
      });
    } catch (err) {
      throw new Error(
        `Failed to update system prototype: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
      );
    }
  };

  return (
    <SystemPrototypeDetaisContextProvider setEditedSection={setEditedSection}>
      <SystemPrototypeDetailsCustomView
        data={enrichedPrototype || prototype}
        type={template?.spec.type}
      />
      <EditSystemPrototypeSidebar
        prototype={prototype}
        template={template}
        editedSection={editedSection}
        onClose={handleCloseEdit}
        onSave={handleSaveEdit}
        layouts={layouts}
      />
    </SystemPrototypeDetaisContextProvider>
  );
};
